import {TTypeApplication, TTypeConstructor} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {Type} from '@packages/systems/wfdl';
import {generateGuid} from '@packages/utilities/guid';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import type {NodePojo} from '@packages/systems/core/nodepojo';

const pluginName = 'DynamoGateway';

const isJSONNode = (node: unknown): node is JSONNode =>
  typeof node === 'object' &&
  node !== null &&
  '_id' in node &&
  !('id' in node) &&
  (('type' in node && typeof node.type === 'string') ||
    ('text' in node && node.text === true));

export const TDynamoBoolean = TTypeConstructor(
  qualify(pluginName, typeName('Boolean'))
);

export const TDynamoPrice = TTypeConstructor(
  qualify(pluginName, typeName('CommercePrice'))
);

export const TDynamoDate = TTypeConstructor(
  qualify(pluginName, typeName('Date'))
);

export const TDynamoDateTime = TTypeConstructor(
  qualify(pluginName, typeName('DateTime'))
);

export const TDynamoEmail = TTypeConstructor(
  qualify(pluginName, typeName('Email'))
);

export const TDynamoFileRef = TTypeConstructor(
  qualify(pluginName, typeName('FileRef'))
);

export const TDynamoFullSlug = TTypeConstructor(
  qualify(pluginName, typeName('FullSlug'))
);

export const TDynamoImage = TTypeConstructor(
  qualify(pluginName, typeName('Image'))
);

export const TDynamoMultiImage = TTypeConstructor(
  qualify(pluginName, typeName('MultiImage'))
);

export const TDynamoCurrentItemRef = (arg: Type) =>
  TTypeApplication({
    con: TTypeConstructor(qualify(pluginName, typeName('CurrentItemRef'))),
    arg,
  });

export const TDynamoItemRef = (arg: Type) =>
  TTypeApplication({
    con: TTypeConstructor(qualify(pluginName, typeName('ItemRef'))),
    arg,
  });

export const TDynamoItemRefSet = (arg: Type) =>
  TTypeApplication({
    con: TTypeConstructor(qualify(pluginName, typeName('ItemRefSet'))),
    arg,
  });

export const TDynamoLink = TTypeConstructor(
  qualify(pluginName, typeName('Link'))
);

export const TDynamoNumber = TTypeConstructor(
  qualify(pluginName, typeName('Number'))
);

export const TDynamoOption = (arg: Type) =>
  TTypeApplication({
    con: TTypeConstructor(qualify(pluginName, typeName('Option'))),
    arg,
  });

export const TDynamoPhone = TTypeConstructor(
  qualify(pluginName, typeName('Phone'))
);

export const TDynamoPlainText = TTypeConstructor(
  qualify(pluginName, typeName('PlainText'))
);

export const TDynamoRichText = TTypeConstructor(
  qualify(pluginName, typeName('RichText'))
);

export const TDynamoSlug = TTypeConstructor(
  qualify(pluginName, typeName('Slug'))
);

export const TDynamoVideoLink = TTypeConstructor(
  qualify(pluginName, typeName('VideoLink'))
);

export const TDynamoRefValue = (collectionIdArg: Type) =>
  TTypeApplication({
    con: TTypeConstructor(qualify(pluginName, typeName('RefValue'))),
    arg: collectionIdArg,
  });

export const getDynamoRichTextToRichTextChildrenImpl = <T>(
  evaluateJsonNodes: (jsonNodes: JSONNode[]) => T
) => {
  return (dynamoRichText: unknown): NodePojo[] | T => {
    if (
      dynamoRichText === null ||
      (typeof dynamoRichText === 'object' &&
        dynamoRichText &&
        'tag' in dynamoRichText &&
        dynamoRichText.tag === 'None')
    ) {
      return [];
    }

    if (
      typeof dynamoRichText !== 'object' ||
      !dynamoRichText ||
      !('val' in dynamoRichText)
    ) {
      return [];
    }

    const richTextValue = dynamoRichText.val;

    if (typeof richTextValue === 'string') {
      return [
        {
          id: generateGuid(),
          children: [],
          pluginType: 'Dynamo',
          type: 'InterimLegacyRichTextValue',
          data: {
            value: richTextValue,
          },
          text: false,
        },
      ];
    }

    if (
      Array.isArray(richTextValue) &&
      richTextValue[0] &&
      isJSONNode(richTextValue[0])
    ) {
      return evaluateJsonNodes(richTextValue);
    }

    return [];
  };
};
